import React from 'react';
import { Link } from 'react-router-dom';
import {
    GraduationCap,
    Building2,
    Shield,
    Users,
    CheckCircle2,
    ArrowRight,
    Server,
    Cloud,
    Laptop,
    Lock
} from 'lucide-react';
import PageLayout from '../Layout/PageLayout';
import PageHeader from '../PageHeader/PageHeader';
import ModernCTA from '../CTA/ModernCTA';
import './ModernSector.scss';

const ModernSled = () => {
    const stats = [
        { number: '200+', label: 'SLED Clients' },
        { number: '50', label: 'States Served' },
        { number: '99%', label: 'Client Satisfaction' },
        { number: '24/7', label: 'Support' }
    ];

    const services = [
        {
            icon: <Building2 size={32} />,
            title: 'State Government',
            description: 'Comprehensive IT solutions for state agencies and departments.',
            features: ['Agency Systems', 'Citizen Services', 'Data Management', 'Security Solutions']
        },
        {
            icon: <Users size={32} />,
            title: 'Local Government',
            description: 'Technology solutions for cities, counties, and municipalities.',
            features: ['Smart City Tech', 'Public Safety', 'Administrative Systems', 'GIS Solutions']
        },
        {
            icon: <GraduationCap size={32} />,
            title: 'K-12 Education',
            description: 'Supporting schools with modern educational technology.',
            features: ['Classroom Tech', 'Student Devices', 'Network Infrastructure', 'Security']
        },
        {
            icon: <Shield size={32} />,
            title: 'Higher Education',
            description: 'Enterprise solutions for colleges and universities.',
            features: ['Research Computing', 'Campus Networks', 'Learning Platforms', 'Cybersecurity']
        }
    ];

    const capabilities = [
        'Cooperative purchasing agreements',
        'State contract compliance',
        'E-Rate program expertise',
        'CIPA and FERPA compliance',
        'Rapid deployment capabilities',
        'Dedicated account management'
    ];

    const techOfferings = [
        { name: 'Cloud Services', icon: <Cloud size={24} /> },
        { name: 'Infrastructure', icon: <Server size={24} /> },
        { name: 'End-User Devices', icon: <Laptop size={24} /> },
        { name: 'Security', icon: <Lock size={24} /> }
    ];

    return (
        <PageLayout>
            <PageHeader
                title="SLED"
                subtitle="Simplify Complex Technology. Focus on Your Mission."
                breadcrumbs={[
                    { label: 'Industries', path: '/serve' },
                    { label: 'SLED' }
                ]}
            />

            {/* Hero Section */}
            <section className="jk-sector-hero">
                <div className="jk-sector-hero__container">
                    <div className="jk-sector-hero__grid">
                        <div className="jk-sector-hero__content">
                            <span className="jk-section-label">
                                <Building2 size={16} />
                                State, Local & Education
                            </span>
                            <h2 className="jk-sector-hero__title">
                                Supporting
                                <span className="highlight"> State & Local Governments</span>, K-12, and Higher Education
                            </h2>
                            <p className="jk-sector-hero__description">
                                We help SLED organizations simplify complex technology challenges so you
                                can focus on your core mission. From classroom technology to smart city
                                infrastructure, we deliver solutions that make a difference.
                            </p>
                            <div className="jk-sector-hero__buttons">
                                <Link to="/contact" className="jk-btn jk-btn--primary">
                                    Get Started
                                    <ArrowRight size={20} />
                                </Link>
                                <Link to="/service" className="jk-btn jk-btn--secondary">
                                    View Solutions
                                </Link>
                            </div>
                        </div>

                        <div className="jk-sector-hero__stats">
                            {stats.map((stat, index) => (
                                <div key={index} className="jk-sector-hero__stat">
                                    <span className="jk-sector-hero__stat-number">{stat.number}</span>
                                    <span className="jk-sector-hero__stat-label">{stat.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="jk-sector-services">
                <div className="jk-sector-services__container">
                    <div className="jk-sector-services__header">
                        <span className="jk-section-label">
                            <GraduationCap size={16} />
                            SLED Solutions
                        </span>
                        <h2 className="jk-sector-services__title">
                            Tailored for
                            <span className="highlight"> Your Sector</span>
                        </h2>
                        <p className="jk-sector-services__subtitle">
                            Technology solutions designed for state, local government, and education
                        </p>
                    </div>

                    <div className="jk-sector-services__grid">
                        {services.map((service, index) => (
                            <div key={index} className="jk-sector-services__card">
                                <div className="jk-sector-services__card-icon">
                                    {service.icon}
                                </div>
                                <h3 className="jk-sector-services__card-title">{service.title}</h3>
                                <p className="jk-sector-services__card-description">{service.description}</p>
                                <ul className="jk-sector-services__card-features">
                                    {service.features.map((feature, idx) => (
                                        <li key={idx}>
                                            <CheckCircle2 size={14} />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Capabilities Section */}
            <section className="jk-sector-capabilities">
                <div className="jk-sector-capabilities__container">
                    <div className="jk-sector-capabilities__grid">
                        <div className="jk-sector-capabilities__content">
                            <span className="jk-section-label">
                                <Shield size={16} />
                                Our Expertise
                            </span>
                            <h2 className="jk-sector-capabilities__title">
                                Why SLED Organizations
                                <span className="highlight"> Choose Us</span>
                            </h2>
                            <p className="jk-sector-capabilities__text">
                                We understand the unique procurement requirements, compliance needs, and
                                budget constraints of SLED organizations. Our expertise helps you navigate
                                complex purchasing processes while getting the best technology solutions.
                            </p>

                            <ul className="jk-sector-capabilities__list">
                                {capabilities.map((item, index) => (
                                    <li key={index} className="jk-sector-capabilities__item">
                                        <CheckCircle2 size={20} className="jk-sector-capabilities__item-icon" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>

                            <Link to="/contact" className="jk-btn jk-btn--primary">
                                Contact Our SLED Team
                                <ArrowRight size={20} />
                            </Link>
                        </div>

                        <div className="jk-sector-capabilities__visual">
                            <div className="jk-sector-capabilities__tech-grid">
                                {techOfferings.map((tech, index) => (
                                    <div key={index} className="jk-sector-capabilities__tech-card">
                                        <div className="jk-sector-capabilities__tech-icon">
                                            {tech.icon}
                                        </div>
                                        <span className="jk-sector-capabilities__tech-name">{tech.name}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="jk-sector-compliance">
                                <h3 className="jk-sector-compliance__title">Compliance & Contracts</h3>
                                <p className="jk-sector-compliance__text">
                                    We work with major cooperative purchasing programs
                                </p>
                                <ul className="jk-sector-compliance__list">
                                    <li><CheckCircle2 size={16} /> NASPO ValuePoint</li>
                                    <li><CheckCircle2 size={16} /> E-Rate Certified</li>
                                    <li><CheckCircle2 size={16} /> State Contracts</li>
                                    <li><CheckCircle2 size={16} /> FERPA Compliant</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <ModernCTA />
        </PageLayout>
    );
};

export default ModernSled;
